import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { liveNewsOptions } from "../../../lib/liveNews";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { MAX_PAGE_SIZE, fetchClientsPage } from "../services/clientsService";
import { countClientNews } from "../clientNews";
import { clientsNewsSince } from "../openedClients";
import { useOpenedClients } from "./useOpenedClients";
import { useWaitingSubmissions } from "./useWaitingSubmissions";

/**
 * How many clients have something new about them — the number the rail carries
 * beside My clients.
 *
 * Two different arrivals are behind that one number, because to the person
 * reading the rail they are the same errand: a client who has landed on the
 * books and nobody here has opened, and a client whose pre-consultation
 * answers have come back and nobody here has read. See `clientNews`, which is
 * where that reading lives.
 *
 * Both are counted, even where they are true of the same client. A badge that
 * only counted heads would sit unchanged when a client already on it sent
 * their answers back — and the arrival this feature exists to announce would
 * pass without the number moving, which is the one thing a badge has to do.
 *
 * Worked out here rather than asked for: the backend counts clients, not unread
 * ones, because whether a record has been read is this browser's own
 * bookkeeping (see `openedClients`). So a page of records is fetched and what
 * has already been seen is subtracted.
 *
 * One page, the largest the endpoint serves, and the unfiltered roster rather
 * than the three lists My clients splits it into — the rail asks one question
 * and should cost one request. A practice with more than a hundred clients
 * nobody has ever opened is one where the exact number has stopped meaning
 * anything; the badge says "99+" at that point.
 *
 * It is its own query rather than a read of the list's: the list is searched,
 * paginated and split by tab according to whatever the screen is showing, and
 * the rail's count must not move because somebody typed in a search box.
 *
 * It polls. A form that comes back while the app is open is news the moment it
 * lands, not the next time somebody reloads — see `liveNews`.
 */

/** The count's own key, kept out of the paginated lists' cache. */
export const NEW_CLIENTS_QUERY_KEY = ["clients", "news"] as const;

export function useNewClientCount(): number {
  const getToken = useTokenGetter();
  const seen = useOpenedClients();
  // Which clients have answers sitting unread. Its own read, because the
  // roster does not carry it — see `useWaitingSubmissions`.
  const waiting = useWaitingSubmissions();

  // A failure is silent: the rail is navigation, and a workspace whose count
  // could not be read is better off with no badge than with an error on it.
  const query = useQuery({
    queryKey: NEW_CLIENTS_QUERY_KEY,
    queryFn: ({ signal }) =>
      fetchClientsPage(getToken, { page: 1, pageSize: MAX_PAGE_SIZE, signal }),
    ...liveNewsOptions,
  });

  const clients = query.data?.clients;

  // The line this browser started counting from, so a practice that has been
  // running for a year does not meet the badge as its own back catalogue. Read
  // rather than held in state: it is written once and never moves. See
  // `clientsNewsSince`.
  const since = clientsNewsSince();

  return useMemo(
    () => countClientNews(clients ?? [], seen, since, waiting),
    [clients, seen, since, waiting]
  );
}

export default useNewClientCount;
